
import { Seat, SeatType, Attendee, LogicalCoords } from '../types';

// 修复：识别“王辉（大）/王辉(小)”这类括号标注，把括号内容当作身份备注处理。
export const parseAttendeeLabel = (rawName: string): { name: string; remark?: string } => {
    const trimmed = rawName.trim();
    const match = trimmed.match(/^(.+?)[（(]([^（）()]+)[）)]$/);

    if (match) {
        const name = match[1].trim();
        const remark = match[2].trim();
        if (name && remark) {
            return { name, remark };
        }
    }

    return { name: trimmed };
};

// 修复：处理名单时统一人名格式——去掉所有空白字符，并去掉英文/中文括号及其内容。
export const normalizeAttendeeNameFormat = (rawName: string): string => {
    return (rawName || '')
        .replace(/\s+/g, '')
        .replace(/[(（][^()（）]*[)）]/g, '');
};

// 修复：统一人员身份 key，避免重名和括号标注在排座/查询时被当成完全无关的人。
export const normalizeAttendeeIdentity = (name: string, remark?: string): { name: string; remark?: string } => {
    const parsed = parseAttendeeLabel(name || '');
    const normalizedRemark = (remark || parsed.remark || '').trim();

    return {
        name: parsed.name,
        remark: normalizedRemark || undefined
    };
};

export const getAttendeeIdentityKey = (name: string, remark?: string): string => {
    const identity = normalizeAttendeeIdentity(name, remark);
    return `${identity.name}\u0000${identity.remark || ''}`;
};

export const getAttendeeFullLabel = (name: string, remark?: string): string => {
    const identity = normalizeAttendeeIdentity(name, remark);
    return identity.remark ? `${identity.name}（${identity.remark}）` : identity.name;
};

export const attendeeMatchesQuery = (name: string, remark: string | undefined, query: string): boolean => {
    const target = normalizeAttendeeIdentity(name, remark);
    const search = normalizeAttendeeIdentity(query);

    if (!target.name || !search.name) return false;

    if (search.remark) {
        return target.name === search.name && target.remark === search.remark;
    }

    return target.name === search.name;
};

export const attendeeIncludesQuery = (name: string, remark: string | undefined, query: string): boolean => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return false;

    const target = normalizeAttendeeIdentity(name, remark);
    return target.name.includes(trimmedQuery) || getAttendeeFullLabel(name, remark).includes(trimmedQuery);
};

// Helper to generate initial grid (Pure rectangular grid)
export const generateGrid = (rows: number, cols: number): Seat[] => {
  const seats: Seat[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      seats.push({
        id: `r${r}-c${c}`,
        row: r,
        col: c,
        type: SeatType.STANDARD,
      });
    }
  }
  return seats;
};

// Calculate Logical Rows and Cols (Skip rows/cols without STANDARD seats)
export const calculateLogicalPositions = (seats: Seat[], totalRows: number, totalCols: number): Map<string, LogicalCoords> => {
    const activeRows = new Set<number>();
    const activeCols = new Set<number>();

    // 1. Identify rows and cols that contain at least one STANDARD seat
    seats.forEach(s => {
        if (s.type === SeatType.STANDARD) {
            activeRows.add(s.row);
            activeCols.add(s.col);
        }
    });

    // 2. Create Mappings (Physical Index -> Logical Number)
    const sortedRows = Array.from(activeRows).sort((a, b) => a - b);
    const sortedCols = Array.from(activeCols).sort((a, b) => a - b);

    const rowMap = new Map<number, number>();
    sortedRows.forEach((physRow, index) => rowMap.set(physRow, index + 1));

    const colMap = new Map<number, number>();
    sortedCols.forEach((physCol, index) => colMap.set(physCol, index + 1));

    // 3. Build Result Map
    const coordsMap = new Map<string, LogicalCoords>();
    seats.forEach(s => {
        const logicalRow = rowMap.get(s.row);
        const logicalCol = colMap.get(s.col);
        
        coordsMap.set(s.id, {
            rowLabel: logicalRow || 0,
            colLabel: logicalCol || 0,
            isValid: logicalRow !== undefined && logicalCol !== undefined && s.type === SeatType.STANDARD
        });
    });

    return coordsMap;
};

// "Global Axis Center-Out" Sorting Algorithm
// Updated to support spacing seat logic
export const getPriorityOrder = (seats: Seat[], totalRows: number, totalCols: number, useSpacing: boolean = false, spacingRowCount: number = 0): Seat[] => {
  const sortedSeats: Seat[] = [];
  
  // Calculate the physical center of the entire grid
  const gridCenter = (totalCols - 1) / 2;

  // 修复：支持“前 N 排间隔、其余不间隔”。排号按用户看到的逻辑排号计算（跳过没有普通座位的行）。
  const activePhysicalRows = new Set<number>();
  seats.forEach(s => {
    if (s.type === SeatType.STANDARD) activePhysicalRows.add(s.row);
  });
  const sortedActiveRows = Array.from(activePhysicalRows).sort((a, b) => a - b);
  const logicalRowByPhysical = new Map<number, number>();
  sortedActiveRows.forEach((physRow, index) => logicalRowByPhysical.set(physRow, index + 1));

  for (let r = 0; r < totalRows; r++) {
    // Get all valid seats in this row
    const rowStandardSeats = seats.filter(s =>
      s.row === r && 
      s.type === SeatType.STANDARD
    );

    // 修复：间隔模式下，手动指定且已坐人的座位也要参与相邻判断，避免自动排座坐到其左右两侧。
    const manualOccupiedSeats = rowStandardSeats.filter(s =>
      s.isManual &&
      !!s.attendeeName?.trim()
    );

    const rowSeats = rowStandardSeats.filter(s =>
      !s.isManual
    );

    if (rowSeats.length === 0) continue;

    // Sort based on distance from the global physical center
    rowSeats.sort((a, b) => {
        const distA = Math.abs(a.col - gridCenter);
        const distB = Math.abs(b.col - gridCenter);

        if (Math.abs(distA - distB) < 0.001) {
            return a.col - b.col;
        }
        return distA - distB;
    });

    // 该行是否采用间隔就坐：开关开启时全部间隔；开关关闭时仅前 spacingRowCount 排间隔。
    const rowUsesSpacing = useSpacing || (spacingRowCount > 0 && (logicalRowByPhysical.get(r) ?? 0) <= spacingRowCount);

    if (rowUsesSpacing) {
        // Implement Spacing Logic: 
        // Iterate through candidates (from center out) and pick only if not physically adjacent to any already picked
        const occupiedForSpacing: Seat[] = [...manualOccupiedSeats];
        const pickedInRow: Seat[] = [];
        for (const candidate of rowSeats) {
            const isAdjacent = occupiedForSpacing.some(p => Math.abs(p.col - candidate.col) === 1);
            if (!isAdjacent) {
                pickedInRow.push(candidate);
                occupiedForSpacing.push(candidate);
            }
        }
        sortedSeats.push(...pickedInRow);
    } else {
        sortedSeats.push(...rowSeats);
    }
  }

  return sortedSeats;
};

export const processAttendees = (rawText: string, leaveText: string): { valid: Attendee[], duplicates: { name: string, originalIndex: number }[] } => {
    const parseEntries = (text: string) => text
        .split(/[\n,]+/)
        .map((value, originalIndex) => ({ rawName: value.trim(), originalIndex }))
        .filter(item => item.rawName.length > 0);
    
    const allEntries = parseEntries(rawText);
    const leaveNames = new Set(parseEntries(leaveText).map(item => normalizeAttendeeNameFormat(item.rawName)));

    // Filter out leave names first（请假/剔除名单与录入名单使用同一套名称规范化，避免空格/括号导致无法匹配）
    const activeEntries = allEntries.filter(item => !leaveNames.has(normalizeAttendeeNameFormat(item.rawName)));

    // Count occurrences
    const nameCounts = new Map<string, number>();
    const parsedEntries = activeEntries.map(item => {
        const parsed = parseAttendeeLabel(normalizeAttendeeNameFormat(item.rawName));
        return { ...item, ...parsed };
    });
    parsedEntries.forEach(item => {
        const key = getAttendeeIdentityKey(item.name, item.remark);
        nameCounts.set(key, (nameCounts.get(key) || 0) + 1);
    });

    const valid: Attendee[] = [];
    const duplicates: { name: string, originalIndex: number }[] = [];

    // Iterate through the raw list to preserve order and capture every instance
    parsedEntries.forEach((item) => {
        const count = nameCounts.get(getAttendeeIdentityKey(item.name, item.remark)) || 0;
        if (count > 1) {
            // Add every instance to duplicates queue so user processes them one by one
            duplicates.push({ name: item.name, originalIndex: item.originalIndex });
        } else {
            valid.push({
                id: crypto.randomUUID(),
                name: item.name,
                remark: item.remark,
                status: 'pending',
                originalIndex: item.originalIndex
            });
        }
    });

    return { valid, duplicates };
};

export const generateCSV = (seats: Seat[], rows: number, cols: number): string => {
    // Recalculate logic coords for export to ensure consistency
    const logicalMap = calculateLogicalPositions(seats, rows, cols);

    // Simple CSV Format: Row,Col,Type,Name,Remark
    const header = "显示排号,显示座号,物理行,物理列,类型,姓名,备注,标签\n";
    const body = seats.map(s => {
        const coords = logicalMap.get(s.id);
        
        let rowDisplay = '';
        let colDisplay = '';

        if (s.type === SeatType.STAGE) {
            rowDisplay = '主席台';
            colDisplay = '';
        } else if (coords?.isValid) {
            rowDisplay = coords.rowLabel.toString();
            colDisplay = coords.colLabel.toString();
        } else {
            rowDisplay = '-';
            colDisplay = '-';
        }

        return `${rowDisplay},${colDisplay},${s.row},${s.col},${s.type},"${s.attendeeName || ''}","${s.attendeeRemark || ''}","${s.label || ''}"`;
    }).join("\n");
    return header + body;
};

// Generate a simple TXT list of names sorted by: Row 1 Center -> Sides, Row 2 Center -> Sides...
export const generateSeatOrderList = (seats: Seat[], rows: number, cols: number): string => {
    // 1. Filter occupied seats (Only Standard) that have a name
    const occupiedSeats = seats.filter(s => 
        s.type === SeatType.STANDARD && 
        s.attendeeName && s.attendeeName.trim() !== ''
    );

    const gridCenter = (cols - 1) / 2;

    // 2. Sort
    occupiedSeats.sort((a, b) => {
        // Primary: Row index (Ascending)
        if (a.row !== b.row) return a.row - b.row;

        // Secondary: Distance from center
        const distA = Math.abs(a.col - gridCenter);
        const distB = Math.abs(b.col - gridCenter);

        // Tie-breaker for symmetry (Left side first if equidistant)
        // e.g. center is 4.5. Col 4 dist is 0.5, Col 5 dist is 0.5.
        // a.col - b.col ensures 4 comes before 5.
        if (Math.abs(distA - distB) < 0.001) {
            return a.col - b.col;
        }

        return distA - distB;
    });

    // 3. Map to names
    return occupiedSeats.map(s => s.attendeeName).join('\n');
};
